import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { useForm } from 'react-hook-form';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { ModalOrderCard } from './subComponents/ModalOrderCard';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import Select from 'react-select';
import { CustomRadioOption } from '../Select/SelectCustomOptions';
import useAvailabilities from '../../hooks/useAvailabilities';
import Dialog, { DialogAlignments, DialogTypes } from '../Dialog/Dialog';
import { DATE_FORMATS } from '../../../constants';
import { ModalDrawerResponse } from './ModalDrawerResponse';
import { ModalDrawerExitFlow } from './ModalDrawerExitFlow';
import { getRequestType } from '../../hooks/useOrderListUtils';
import { Loader } from '../common';
import { calculatePriceDifference } from '../../hooks/useOrderListUtils';

const modalTitle = TRANSLATIONS.RESCHEDULE_THE_ACTIVITY;
const REQUEST_LABEL = 'new_date';

//NotCompleted = onHold and confirmed orders
const ModalNotCompletedReschedule = ({
  isOpen,
  setIsOpen,
  token,
  order,
  customGoBackFunc,
  setResponseModal,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const orderId = order.id;
  const orderItems = order?.items || [];
  const productId = order.product.id;
  const listingId = order.listing.id;
  const firstStep = 1;
  const total = order.total;
  const requestType = getRequestType(order.status, REQUEST_LABEL);

  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );
  const { register, handleSubmit, reset } = useForm();
  const datetimeFormRef = useRef(null);

  const { availabilitiesPerDate, date, setDate, isDayBlocked } =
    useAvailabilities(listingId, productId);
  const [responseModalState, setResponseModalState] = useState({
    isOpen: false,
    isSuccess: false,
    responseContent: '',
    modalTitle: '',
  });
  const actualSetResponseModal = setResponseModal || setResponseModalState;
  const [step, setStep] = useState(firstStep);
  const [focus, setFocus] = useState(false);
  const [formData, setFormData] = useState({});
  const [time, setTime] = useState(null);
  const [price, setPrice] = useState(null);
  const [dateError, setDateError] = useState(null);
  const [showModalExit, setShowModalExit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [priceDifference, setPriceDifference] = useState(0);

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setShowModalExit(false);
      setDate(null);
      setTime(null);
      setPrice(null);
      setPriceDifference(0);
      reset();
      setDateError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!!availabilitiesPerDate.length) {
      setTime(availabilitiesPerDate[0]);
      setPrice(availabilitiesPerDate[0].price);
      setPriceDifference(
        calculatePriceDifference(availabilitiesPerDate[0].price, orderItems)
      );
    }
  }, [availabilitiesPerDate]);

  const goBackFun = () => {
    if (customGoBackFunc) {
      customGoBackFunc();
    } else {
      setStep(step - 1);
    }
  };

  useEffect(() => {
    if (price) {
      setPriceDifference(calculatePriceDifference(price, orderItems));
    }
  }, [price]);

  const onSubmit = async () => {
    //TODO: improve this part, there are two concat
    let newDate = formData.date + ' ' + formData.time;
    let formattedDateTime = moment(newDate, 'DD/MM/YYYY HH:mm').format(
      'YYYY-MM-DD HH:mm:ss'
    );

    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: requestType,
          requested_booking_datetime: formattedDateTime,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: true,
        responseContent:
          TRANSLATIONS.YOUR_DATE_CHANGE_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED,
        modalTitle: modalTitle,
      });
    } catch (error) {
      // console.log(error);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: false,
        responseContent:
          TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER,
        modalTitle: modalTitle,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const handleNextStep = (data, event) => {
    event.preventDefault();

    if (!data.date && data.date === '') {
      setDateError(TRANSLATIONS.SELECTION_REQUIRED);
      return;
    }

    const mergedData = { ...formData, ...data };
    setFormData(mergedData);
    setStep(step + 1);
  };

  const datetimeForm = (
    <form ref={datetimeFormRef} onSubmit={handleSubmit(handleNextStep)}>
      <div className="modal-datetime-form d-flex flex-column gap-xs">
        <div
          className={`modal-datetime-form__input ${dateError ? 'modal-datetime-form__input--errored' : ''
            }`}
        >
          <div className="relative">
            <SingleDatePicker
              id="datetime-form-input"
              numberOfMonths={1}
              hideKeyboardShortcutsPanel={true}
              keepOpenOnDateSelect={false}
              displayFormat={'D MMM YYYY'}
              isOutsideRange={() => false}
              date={date}
              placeholder={TRANSLATIONS.SELECT_A_DATE}
              onDateChange={(value) => {
                setDate(value), setDateError(null);
              }}
              focused={focus}
              onFocusChange={({ focused }) => setFocus(focused)}
              isDayBlocked={isDayBlocked}
              readOnly
            />
            <div className={'modal-datetime-form__input-label'}>
              <label>{TRANSLATIONS.EDIT}</label>
            </div>
          </div>
          {dateError && (
            <div
              className={
                'w-100% pt-3xs modal-datetime-form__input-label--errored'
              }
            >
              {dateError}
            </div>
          )}
        </div>
        <div
          className={`modal-datetime-form__input ${dateError ? 'modal-datetime-form__input--errored' : ''
            }`}
        >
          <div className="relative">
            <Select
              classNamePrefix="modal-datetime-form__time-select"
              key={time?.id || 'modal-datetime-form'}
              onMenuOpen={() => setFocus(null)}
              placeholder={TRANSLATIONS.SELECT_TIME}
              inputProps={{ readOnly: true }}
              isSearchable={false}
              isDisabled={!date}
              options={availabilitiesPerDate}
              onChange={(value) => {
                setTime(value);
                setPrice(value.price);
              }}
              defaultValue={time}
              isModalOpen={true}
              components={{
                IndicatorSeparator: () => null,
                Option: (props) => (
                  <CustomRadioOption {...props} selected={time?.label} />
                ),
                DropdownIndicator: () => null,
              }}
            />
            <div
              className={`modal-datetime-form__input-label ${!date ? 'modal-datetime-form__input-label--is-disabled' : ''
                }`}
            >
              <label>{TRANSLATIONS.EDIT}</label>
            </div>
          </div>
          {dateError && (
            <div
              className={
                'w-100% pt-3xs modal-datetime-form__input-label--errored'
              }
            >
              {dateError}
            </div>
          )}
        </div>
        {priceDifference !== 0 && (
          <Dialog
            align={DialogAlignments.row}
            type={DialogTypes.feature}
            content={
              priceDifference < 0
                ? TRANSLATIONS.SELECTED_DATE_REQUIRES_REFUND.replace(
                  '{{priceDifference}}',
                  Math.abs(priceDifference)
                )
                : TRANSLATIONS.SELECTED_DATE_REQUIRES_EXTRA_CHARGE.replace(
                  '{{priceDifference}}',
                  Math.abs(priceDifference)
                )
            }
            icon={
              <BetaIcon
                name={'info'}
                color={IconColors.regular}
                size={IconSizes[24]}
              />
            }
          />
        )}
        <input
          type="hidden"
          name={'date'}
          ref={register}
          value={(date && date.format(DATE_FORMATS.USER_FORM_FE)) || ''}
        />
        <input
          type="hidden"
          name={'time'}
          ref={register}
          value={(time && time.label) || ''}
        />
      </div>
    </form>
  );

  const footerContent = (
    <>
      <div className="d-flex gap-m pb-m pl-xl pr-xl">
        <Button
          disabled={isLoading}
          classNames={'flex-1 pr-m pl-m'}
          size={ButtonSizes.s}
          style={ButtonStyles.empty}
          label={TRANSLATIONS.CANCEL}
          onClick={() => setShowModalExit(true)}
        />
        <Button
          disabled={isLoading}
          classNames={'flex-1 pr-m pl-m'}
          size={ButtonSizes.s}
          style={ButtonStyles.secondary}
          label={TRANSLATIONS.CONTINUE}
          onClick={() => {
            if (step === 1) {
              datetimeFormRef.current?.dispatchEvent(
                new Event('submit', { cancelable: true })
              );
            } else if (step === 2) {
              onSubmit();
            }
          }}
        />
      </div>
    </>
  );

  const firstStepModal = (
    <ModalDrawer
      fullDrawer={true}
      goBack={customGoBackFunc}
      isOpen={isOpen && step === 1 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p2}
            weight={TypographyWeights.bold}
          >
            {TRANSLATIONS.WHEN_WOULD_YOU_LIKE_TO_DO_THE_ACTIVITY}
          </Typography>
          {datetimeForm}
        </div>
      }
      footer={footerContent}
    />
  );

  const secondStepModal = (
    <ModalDrawer
      fullDrawer={true}
      goBack={goBackFun}
      isOpen={isOpen && step === 2 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        isLoading ? (
          <div className="d-flex justify-center text-center">
            <Loader loading={isLoading} />
          </div>
        ) : (
          <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
            <Dialog
              align={DialogAlignments.row}
              type={DialogTypes.feature}
              content={TRANSLATIONS.HOW_IT_WORKS_RESCHEDULE_ON_HOLD}
              icon={
                <BetaIcon
                  name={'info'}
                  color={IconColors.regular}
                  size={IconSizes[24]}
                />
              }
            />
            <div className="d-flex flex-column gap-xs">
              <div
                className="typography__p3 typography__neutral text-left"
                dangerouslySetInnerHTML={{
                  __html: TRANSLATIONS.DATE_CHANGE_REQUEST_SUMMARY,
                }}
              />
              <ModalOrderCard
                order={order}
                editRequest={() => setStep(1)}
                toReschedule={true}
                newDate={formData.date + ' - ' + formData.time}
                priceDifference={priceDifference}
              />
            </div>
          </div>
        )
      }
      footer={footerContent}
    />
  );

  const responseModalDrawer = (
    <ModalDrawerResponse
      isOpen={responseModalState.isOpen}
      setIsOpen={actualSetResponseModal}
      isSuccess={responseModalState.isSuccess}
      modalTitle={responseModalState.modalTitle}
      responseContent={responseModalState.responseContent}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  return (
    <>
      {responseModalDrawer}
      {firstStepModal}
      {secondStepModal}
      <ModalDrawerExitFlow
        isOpen={showModalExit}
        setIsOpen={setShowModalExit}
        closeAction={() => setIsOpen(false)}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      />
    </>
  );
};

ModalNotCompletedReschedule.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  customGoBackFunc: PropTypes.func,
  setResponseModal: PropTypes.func,
};

export { ModalNotCompletedReschedule };
